import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { search, type SearchResult } from '@/api/caseSearch';
import { isUserFriendlyErrorMessage } from '@/api/http';
import { useAppErrorStore } from '@/stores/appErrorStore';
import { cloneDeep } from 'lodash';
import { asyncTimeout, logger } from '@/util';

const defaultSearchResult = {
    count: 0,
    pages: {
        current: 0,
        previous: null,
        next: null,
    },
    cases: [],
};

function makeDefaultSearchResult() {
    return cloneDeep(defaultSearchResult);
}

const log = logger('poll-cases');
const POLL_INTERVAL_MILLISECONDS = 10 * 1000;

export const useDashboardStore = defineStore('dashboard', () => {
    const appError = useAppErrorStore();

    const isReady = ref(false);
    const isLoading = ref(true);
    const error = ref('');
    const searchText = ref('');
    const searchFilter = ref('');
    const result = ref<SearchResult>(makeDefaultSearchResult());

    let cancellation: AbortController | null = null;

    const allCasesSearch = computed(() => searchText.value === '');

    async function load(nextPage = 1) {
        // Cancel any existing polling
        cancellation?.abort();
        cancellation = new AbortController();

        isLoading.value = true;
        error.value = '';

        // Start new polling
        appError.catchErrors(() => {
            return _pollCases(
                searchText.value,
                nextPage,
                (response) => {
                    if (isUserFriendlyErrorMessage(response)) {
                        error.value = response;
                    } else {
                        searchFilter.value = searchText.value;
                        result.value = response;
                    }
                    isLoading.value = false;

                    if (!isReady.value) {
                        isReady.value = true;
                    }
                },
                { signal: cancellation?.signal },
            );
        });
    }

    async function nextPage() {
        if (result.value.pages.next) {
            await load(result.value.pages.next);
            window.scrollTo(0, 0);
        }
    }

    async function previousPage() {
        if (result.value.pages.previous) {
            await load(result.value.pages.previous);
            window.scrollTo(0, 0);
        }
    }

    function stop() {
        cancellation?.abort();
    }

    function reset() {
        isReady.value = false;
        isLoading.value = true;
        error.value = '';
        searchText.value = '';
        searchFilter.value = '';
        result.value = makeDefaultSearchResult();
    }

    return {
        isReady,
        isLoading,
        error,
        searchText,
        searchFilter,
        result,
        allCasesSearch,
        load,
        nextPage,
        previousPage,
        stop,
        reset,
    };
});

/**
 * Repeatedly fetch cases until cancelled
 */
async function _pollCases(
    searchText: string,
    page: number,
    onFetch: (result: SearchResult | string) => void,
    options?: {
        signal?: AbortSignal;
    },
): Promise<void> {
    const fetch = async (): Promise<SearchResult | string> => {
        return await search(searchText, page);
    };

    try {
        while (!options?.signal?.aborted) {
            options?.signal?.throwIfAborted();

            // Attempt to fetch cases
            const response = await fetch();

            options?.signal?.throwIfAborted();

            // Call the callback with the result
            onFetch(response);

            options?.signal?.throwIfAborted();

            await asyncTimeout(POLL_INTERVAL_MILLISECONDS, options?.signal);
             
        }
    } catch (e: unknown) {
        if (options?.signal?.aborted) {
            log.debug('cases polling cancelled');
            return;
        }
        throw e;
    }
}
